/**
 * PasscodeSection — change the shared access passcode from Settings
 * (docs/SPEC-monitor2-passcode-cleanup.md §A).
 *
 * Sends `PUT /api/access-key` (the request itself must be unlocked with
 * the current passcode), then updates THIS device's stored key so it
 * stays signed in. Every other device will be prompted for the new
 * passcode the next time it talks to the Pi.
 */
import { useState } from 'react';

import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import { accessKeyApi } from '../../lib/access-key-api';
import { writeAccessKey } from '../../lib/agent-fetch';

const MIN_LENGTH = 6;

export function PasscodeSection() {
  const [newKey, setNewKey] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const tooShort = newKey.length > 0 && newKey.trim().length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && newKey !== confirm;
  const canSave =
    newKey.trim().length >= MIN_LENGTH && newKey === confirm && !busy;

  async function handleSave() {
    setBusy(true);
    setMessage(null);
    const res = await accessKeyApi.change(newKey.trim());
    if (res.ok) {
      writeAccessKey(newKey.trim());
      setNewKey('');
      setConfirm('');
      setMessage({
        kind: 'ok',
        text: 'Passcode changed. This device stays unlocked; every other device will ask for the new passcode.',
      });
    } else {
      setMessage({ kind: 'error', text: `Could not change the passcode: ${res.error}` });
    }
    setBusy(false);
  }

  return (
    <Card className="mt-6" data-testid="settings-passcode-section">
      <CardHeader>
        <CardTitle>Passcode</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-xs text-reef-300 mb-4">
          The passcode protects all your tank data. Changing it here updates the
          Pi immediately — no restart needed. Minimum {MIN_LENGTH} characters.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
          <label className="text-sm text-reef-200 flex flex-col gap-1">
            New passcode
            <input
              type="password"
              value={newKey}
              onChange={(e) => {
                setNewKey(e.target.value);
                setMessage(null);
              }}
              className="rounded-md border border-reef-700/60 bg-reef-900/60 px-2.5 py-1.5 text-sm text-reef-100"
              data-testid="passcode-new-input"
            />
          </label>
          <label className="text-sm text-reef-200 flex flex-col gap-1">
            Confirm new passcode
            <input
              type="password"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                setMessage(null);
              }}
              className="rounded-md border border-reef-700/60 bg-reef-900/60 px-2.5 py-1.5 text-sm text-reef-100"
              data-testid="passcode-confirm-input"
            />
          </label>
        </div>
        {tooShort ? (
          <p className="mt-2 text-xs text-amber-300" data-testid="passcode-too-short">
            At least {MIN_LENGTH} characters.
          </p>
        ) : null}
        {mismatch ? (
          <p className="mt-2 text-xs text-amber-300" data-testid="passcode-mismatch">
            The two entries don&apos;t match yet.
          </p>
        ) : null}
        <div className="mt-4">
          <Button
            variant="primary"
            size="sm"
            disabled={!canSave}
            onClick={() => void handleSave()}
            data-testid="passcode-save"
          >
            {busy ? 'Changing…' : 'Change passcode'}
          </Button>
        </div>
        {message ? (
          <p
            className={`mt-3 text-sm ${message.kind === 'ok' ? 'text-emerald-300' : 'text-red-300'}`}
            data-testid="passcode-message"
          >
            {message.text}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
